import { createAsyncThunk } from '@reduxjs/toolkit'

import { callErrorMsg } from 'helpers/errorMsg'
import { toastFailed, toastSuccess } from 'utils/toast'
import { downloadFileUrl } from '@astronautsid/wpe-utils'
import {
  fetchRlSourcingApi,
  fetchDeleteReasonsApi,
  deleteVendorsApi,
  updateVendorRlDetailApi,
  fetchVendorCatalogsApi,
  updateVendorCatalogsApi,
  fetchPODraftTotalApi,
  fetchCheckedRlApi,
  saveRlApi,
  fetchRequestUpdateApi,
  fetchRequestChangeDetailsApi,
  postRequestChangesApi,
  downloadRlApi,
  fetchVendorRequestChagesStatusApi,
  getProductVendorFeatureFlagApi,
  getProductVendorsApi,
  fetchUOMs,
} from '../../services/reorderListSourcing'
import {
  type DeleteVendorPayloadType,
  type UpdateRlVendorDetailType,
  type VendorCatalogPayloadType,
  type RlSourcingFilterType,
  type SaveRlRequestType,
  type SaveRlPayloadType,
  type RequestChangesPayloadType,
  type TableCheckedRlType,
} from '../../types/rlSourcing'
import { PageType } from '../../types/rlFilter'
import {
  sliceName,
  setRlFilter,
  setIsLoading,
  setRlSourcingData,
  setDeleteReasons,
  setVendorCatalogs,
  setTotalPoDraft,
  setCheckedRlData,
  setRequestChangesData,
  setRequestChangeDetails,
  setModalState,
  setNewRlPayload,
  setProductVendorFeatureFlag,
  setUOMs,
} from './reorderListSourcingSlice'

export const setSourcingRlFilterAction =
  ({ filter, pageType }: { filter: RlSourcingFilterType; pageType: PageType }) =>
  async (dispatch: StoreDispatchType) => {
    try {
      const newFilter = { ...filter }

      if (pageType === 'history') {
        newFilter.is_deleted = true
        newFilter.statuses = 'CONFIRMED,PARTIAL,ON_CHANGES'
      }

      if (pageType === 'requestUpdate') {
        newFilter.statuses = 'REVIEWED,UPDATED'
      }

      if (pageType === 'sourcing') {
        newFilter.statuses = 'CONFIRMED,PARTIAL'
      }

      if (pageType === 'sc' && !newFilter.statuses) {
        newFilter.statuses = 'REVIEWED,UPDATED'
      }

      await dispatch(setRlFilter(newFilter))

      if (pageType === 'requestUpdate' || pageType === 'sc') {
        await dispatch(fetchRequestUpdateData())
      } else {
        await dispatch(fetchReorderListSourcing())
      }
    } catch (err) {
      callErrorMsg(err)
    }
  }

export const fetchReorderListSourcing = createAsyncThunk(
  `${sliceName}/fetchReorderListSourcing`,
  async (_, { getState, dispatch }) => {
    const {
      reorderListSourcing: { rlSourcingFilter },
    } = getState() as StoreStateType
    dispatch(setIsLoading(true))

    try {
      const { data } = await fetchRlSourcingApi(rlSourcingFilter)
      dispatch(setRlSourcingData(data))
    } catch (err) {
      callErrorMsg(err)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const saveRl = createAsyncThunk(
  `${sliceName}/saveRl`,
  async (
    {
      rlData,
      checkedRl,
      cb,
    }: { rlData: SaveRlPayloadType[]; checkedRl?: TableCheckedRlType[]; cb: () => void },
    { dispatch },
  ) => {
    dispatch(setIsLoading(true))

    try {
      const payload: SaveRlRequestType[] = rlData.map((item) => ({
        reorder_list_vendor_id: item.reorder_list_vendor_id,
        checked: item.checked,
      }))

      const { data } = await saveRlApi(payload)
      if (data?.data?.reorder_list_detail?.length) {
        const newSavedRlData = rlData.filter(
          (item) =>
            !data?.data?.reorder_list_detail?.some(
              (rl) => rl.reorder_list_vendor_id === item.reorder_list_vendor_id,
            ),
        )

        dispatch(
          setModalState({
            type: 'vendor-is-saved',
            selectedRl: [],
            reviewedVendors: data.data.reorder_list_detail?.map((vendor) => ({
              ...vendor,
              vendor_name:
                checkedRl?.find(
                  (item) => item.reorder_list_vendor_id === vendor.reorder_list_vendor_id,
                )?.vendor_name || '',
            })),
          }),
        )
        dispatch(setNewRlPayload(newSavedRlData))
      } else {
        toastSuccess('Reorder list berhasil disimpan')
        cb()
        dispatch(
          setModalState({
            type: '',
            selectedRl: [],
          }),
        )
      }
    } catch (err) {
      callErrorMsg(err)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const fetchDeleteReasons = createAsyncThunk(
  `${sliceName}/fetchDeleteReasons`,
  async (_, { dispatch }) => {
    dispatch(setIsLoading(true))

    try {
      const { data } = await fetchDeleteReasonsApi()
      dispatch(setDeleteReasons(data?.data))
    } catch (err) {
      callErrorMsg(err)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const updateVendorRlDetail = createAsyncThunk(
  `${sliceName}/deleteVendors`,
  async (
    { reorderListId, payload }: { reorderListId: number; payload: UpdateRlVendorDetailType },
    { dispatch },
  ) => {
    dispatch(setIsLoading(true))

    try {
      await updateVendorRlDetailApi(reorderListId, payload)
      await dispatch(fetchReorderListSourcing())
      toastSuccess('Vendor detail berhasil diupdate')
    } catch (err) {
      callErrorMsg(err)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const deleteVendors = createAsyncThunk(
  `${sliceName}/deleteVendors`,
  async ({ payload }: { payload: DeleteVendorPayloadType }, { dispatch }) => {
    dispatch(setIsLoading(true))

    try {
      await deleteVendorsApi(payload)
      await dispatch(fetchReorderListSourcing())
      toastSuccess('Data RL berhasil dihapus')
    } catch (err) {
      callErrorMsg(err)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const fetchVendorCatalogs = (productId: number) => async (dispatch: StoreDispatchType) => {
  try {
    const { data } = await fetchVendorCatalogsApi(productId)
    dispatch(setVendorCatalogs(data.data))
  } catch (err) {
    callErrorMsg(err)
  }
}

export const fetchCommercialVendorCatalogs =
  (productId: number) => async (dispatch: StoreDispatchType) => {
    try {
      const { data } = await getProductVendorsApi(productId)
      const formattedData = data.data?.map((item) => ({
        catalogId: item.id,
        locations: item.locations.map((location) => ({
          locationId: location.id,
          locationName: location.name,
          locationType: location.type,
        })),
        picSourcing: {
          picId: item.pic_sourcing.id,
          picName: item.pic_sourcing.name,
          picEmail: item.pic_sourcing.full_name,
        },
        preference: item.preference,
        price: item.price,
        rebate: item.rebate,
        vendor: {
          vendorId: item.vendor.id,
          vendorName: item.vendor.name,
        },
        pic: {
          picId: 0,
          picName: '',
          picEmail: '',
        },
        scheduled_prices: item.scheduled_prices,
      }))
      dispatch(setVendorCatalogs(formattedData))
    } catch (err) {
      callErrorMsg(err)
    }
  }

export const fetchPODraftTotal = createAsyncThunk(
  `${sliceName}/fetchPODraftTotal`,
  async (_, { dispatch }) => {
    try {
      const { data } = await fetchPODraftTotalApi()
      dispatch(setTotalPoDraft(data.data.total_po_draft))
    } catch (err) {
      callErrorMsg(err)
    }
  },
)

export const fetchCheckedRl = createAsyncThunk(
  `${sliceName}/fetchCheckedRl`,
  async (_, { dispatch }) => {
    try {
      const { data } = await fetchCheckedRlApi()
      dispatch(setCheckedRlData({ reorder_list_vendors: data.data?.reorder_list_vendors }))
    } catch (err) {
      callErrorMsg(err)
    }
  },
)

export const updateVendorCatalogs = createAsyncThunk(
  `${sliceName}/updateVendorCatalogs`,
  async (
    { rlId, payload }: { rlId: number; payload: VendorCatalogPayloadType[] },
    { dispatch },
  ) => {
    dispatch(setIsLoading(true))

    try {
      await updateVendorCatalogsApi(rlId, payload)
      await dispatch(fetchReorderListSourcing())
      toastSuccess('Vendor catalogs berhasil diupdate')
    } catch (err) {
      callErrorMsg(err)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const createRequestChanges = createAsyncThunk(
  `${sliceName}/createRequestChanges`,
  async ({ rlId, data }: { rlId: number; data: RequestChangesPayloadType }, { dispatch }) => {
    try {
      dispatch(setIsLoading(true))
      await postRequestChangesApi(rlId, data)
      await dispatch(fetchReorderListSourcing())
      toastSuccess('Reorder list berhasil diupdate untuk di konfirmasi ke tim SC')
    } catch (err) {
      callErrorMsg(err)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const fetchRequestUpdateData = createAsyncThunk(
  `${sliceName}/fetchRequestUpdateData`,
  async (_, { getState, dispatch }) => {
    const {
      reorderListSourcing: { rlSourcingFilter },
    } = getState() as StoreStateType
    dispatch(setIsLoading(true))

    try {
      const { data } = await fetchRequestUpdateApi(rlSourcingFilter)
      dispatch(setRequestChangesData(data))
    } catch (err) {
      callErrorMsg(err)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const fetchRequestChangeDetail = createAsyncThunk(
  `${sliceName}/fetchRequestChangeDetails`,
  async (rlId: number, { dispatch }) => {
    try {
      const { data } = await fetchRequestChangeDetailsApi(rlId)
      dispatch(setRequestChangeDetails(data?.data))
    } catch (err) {
      callErrorMsg(err)
    }
  },
)

export const fetchDownloadRlUrl = createAsyncThunk(
  `${sliceName}/fetchDownloadRlUrl`,
  async (_, { dispatch }) => {
    dispatch(setIsLoading(true))

    try {
      const { data } = await downloadRlApi()
      if (data?.data?.url === 'EMAIL') {
        toastSuccess('Document Reorder List akan dikirim melalui email')
      } else {
        downloadFileUrl(
          data.data.url,
          `reorder_list ${new Date().toISOString()?.split('T')[0]}.csv`,
        )
      }
    } catch (err) {
      callErrorMsg(err)
      toastFailed('Gagal mengunduh Reorder List')
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const fetchVendorRequestChagesStatus = createAsyncThunk(
  `${sliceName}/fetchVendorRequestChagesStatus`,
  async (
    {
      vendor_ids,
      checkedRl,
      cb,
    }: { vendor_ids: string; checkedRl: TableCheckedRlType[]; cb: () => void },
    { dispatch },
  ) => {
    try {
      dispatch(setIsLoading(true))
      const { data } = await fetchVendorRequestChagesStatusApi(vendor_ids)
      if (data?.data?.vendors?.length) {
        dispatch(
          setNewRlPayload(
            checkedRl?.map((item) => ({
              reorder_list_vendor_id: item.reorder_list_vendor_id,
              checked: true,
            })),
          ),
        )
        dispatch(
          setModalState({
            type: 'vendor-request-change',
            vendorRequestChange: data.data?.vendors,
          }),
        )
      } else {
        cb()
      }
    } catch (err) {
      callErrorMsg(err)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const getProductVendorFeatureFlag = createAsyncThunk(
  `${sliceName}/getProductVendorFeatureFlag`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getProductVendorFeatureFlagApi()
      dispatch(
        setProductVendorFeatureFlag(
          data.data?.FF_ENABLE_VENDOR_CATALOG_MIGRATION_ALLOYDB === 'true',
        ),
      )
    } catch (err) {
      callErrorMsg(err)
    }
  },
)

export const fetchUOMLists = createAsyncThunk(
  `${sliceName}/fetchUOMLists`,
  async (_, { dispatch }) => {
    dispatch(setIsLoading(true))

    try {
      const { data } = await fetchUOMs()
      const uomList = data.data.unit_of_measurements.content || []
      dispatch(setUOMs(uomList))
    } catch (err) {
      callErrorMsg(err)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)
